import { normaliseDomain, readFairDomain } from '../api/fairDomain';

/**
 * The legacy Symfony API (`_legacy/vv-backend`), which still owns visitor login
 * and stand chat: the `user`, `stand_chat` and `stand_chat_message` tables, the
 * exhibitor side (the `/owner/` app) and the reply e-mails all run through it,
 * so the new frontend uses the same endpoints rather than a second
 * implementation that would have to match it field for field.
 *
 * Unlike the new API it has no response envelope: bodies are plain JSON.
 */
const LEGACY_API_URL = (import.meta.env.VITE_LEGACY_API_URL ?? 'https://api.dankarijera.com')
  .trim()
  .replace(/\/+$/, '');

export class LegacyApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'LegacyApiError';
  }

  /** The JWT is missing, invalid or expired (it lives 18 hours). */
  get unauthorized(): boolean {
    return this.status === 401;
  }
}

type QueryValue = string | number | null | undefined;

interface LegacyRequest {
  method?: 'GET' | 'POST';
  query?: Record<string, QueryValue>;
  /** Sent form-encoded, which is what the Symfony controllers read. */
  form?: Record<string, string | number>;
  token?: string | undefined;
}

export async function legacyRequest<T>(
  path: string,
  { method = 'GET', query = {}, form, token }: LegacyRequest = {},
): Promise<T> {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }

  const queryString = search.toString();
  const headers: Record<string, string> = {};

  if (token !== undefined) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  let response: Response;

  try {
    response = await fetch(
      `${LEGACY_API_URL}${path}${queryString === '' ? '' : `?${queryString}`}`,
      {
        method,
        headers,
        // URLSearchParams sets the form content type itself.
        ...(form !== undefined && {
          body: new URLSearchParams(
            Object.entries(form).map(([key, value]) => [key, String(value)]),
          ),
        }),
      },
    );
  } catch (cause) {
    throw new LegacyApiError(cause instanceof Error ? cause.message : 'Network request failed', 0);
  }

  if (!response.ok) {
    throw new LegacyApiError(
      `Request failed with status ${String(response.status)}`,
      response.status,
    );
  }

  return (await response.json()) as T;
}

/**
 * The fair as the legacy API knows it, by domain — the same domain the new API
 * resolves the fair from: the override where one is set (local development,
 * the dev stage), the page's own host otherwise.
 */
export function legacyFairDomain(): string {
  return readFairDomain() ?? normaliseDomain(window.location.hostname);
}
